import React, { useState } from 'react';
import { startShopifyInstall } from '../utils/shopifyInstall';
import './Landing.css';

const features = [
  {
    title: 'Automatic order sync',
    body:
      'New Shopify orders are pushed into Tally as invoices in real time. No CSV exports, no manual data entry.',
  },
  {
    title: 'Tally-ready invoices',
    body:
      'Order data is mapped to Tally vouchers with line items, taxes, and customer details ready for your accountant.',
  },
  {
    title: 'Flexible configuration',
    body:
      'Map ledgers, tax rates, and voucher types once. The app applies your rules to every order going forward.',
  },
  {
    title: 'Sync history & retries',
    body:
      'Every sync is logged with status, payload, and errors. Retry failed orders in one click.',
  },
];

const steps = [
  {
    title: 'Install on Shopify',
    body: 'Connect your Shopify store with one-click OAuth. We never see your customer or product data outside the sync.',
  },
  {
    title: 'Configure Tally mapping',
    body: 'Point the app at your Tally instance and map ledgers, tax rates, and voucher types to match your books.',
  },
  {
    title: 'Sync runs automatically',
    body: 'New orders flow into Tally as soon as they happen. Monitor progress on the dashboard and download exports anytime.',
  },
];

const pricingTeaser = [
  {
    name: 'Starter',
    price: '₹999',
    cycle: '/month',
    orders: 'Up to 100 orders / month',
    features: ['Automatic sync', 'Tally voucher export', 'Email support'],
  },
  {
    name: 'Growth',
    price: '₹2,499',
    cycle: '/month',
    orders: 'Up to 500 orders / month',
    features: ['Everything in Starter', 'Sync history & retries', 'Priority support'],
  },
  {
    name: 'Scale',
    price: '₹4,999',
    cycle: '/month',
    orders: 'Unlimited orders',
    features: ['Everything in Growth', 'Custom field mapping', 'Dedicated support'],
  },
];

const InstallForm: React.FC<{ idPrefix?: string }> = ({ idPrefix = 'top' }) => {
  const [shop, setShop] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await startShopifyInstall(shop);
    } catch (err: any) {
      setError(err?.message || 'Could not start install. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <form className="landing__install-form" onSubmit={handleSubmit}>
        <input
          id={`${idPrefix}-shop-input`}
          type="text"
          className="landing__install-input"
          placeholder="your-store.myshopify.com"
          value={shop}
          onChange={(e) => setShop(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={submitting}
        />
        <button
          type="submit"
          className="landing__install-button"
          disabled={submitting || !shop.trim()}
        >
          {submitting ? 'Redirecting…' : 'Install on Shopify'}
        </button>
      </form>
      <div className="landing__install-hint">
        Enter your Shopify store URL to install. Free 7-day trial, cancel anytime.
      </div>
      {error && <div className="landing__install-error">{error}</div>}
    </>
  );
};

const Landing: React.FC = () => {
  return (
    <div className="landing">
      <nav className="landing__nav">
        <a href="/" className="landing__brand">Shopify Tally Sync</a>
        <div className="landing__nav-links">
          <a href="#features" className="landing__nav-link">Features</a>
          <a href="#how" className="landing__nav-link">How it works</a>
          <a href="/pricing" className="landing__nav-link">Pricing</a>
          <a href="#install" className="landing__nav-cta">Install</a>
        </div>
      </nav>

      <header className="landing__hero" id="install">
        <span className="landing__eyebrow">Shopify · Tally Integration</span>
        <h1>Sync Shopify orders into Tally as invoices, automatically.</h1>
        <p>
          Stop copy-pasting orders into Tally. Connect your Shopify store and every new order flows
          into Tally as a properly mapped invoice — with taxes, line items, and customer details
          intact.
        </p>
        <InstallForm idPrefix="hero" />
      </header>

      <section className="landing__section" id="features">
        <div className="landing__section-header">
          <h2>Everything your accountant needs, on autopilot</h2>
          <p>
            Built for Indian Shopify merchants who use Tally for accounting and want to stop
            wasting hours on month-end reconciliation.
          </p>
        </div>
        <div className="landing__features">
          {features.map((f) => (
            <div key={f.title} className="landing__feature">
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing__section" id="how">
        <div className="landing__section-header">
          <h2>Up and running in minutes</h2>
          <p>Three steps from install to your first synced order.</p>
        </div>
        <div className="landing__steps">
          {steps.map((s) => (
            <div key={s.title} className="landing__step">
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing__section" id="pricing">
        <div className="landing__section-header">
          <h2>Simple pricing that scales with you</h2>
          <p>Start with a 7-day free trial. Upgrade or cancel anytime.</p>
        </div>
        <div className="landing__pricing">
          {pricingTeaser.map((p) => (
            <div key={p.name} className="landing__plan">
              <h3>{p.name}</h3>
              <div className="landing__plan-price">
                {p.price}
                <span>{p.cycle}</span>
              </div>
              <div className="landing__plan-orders">{p.orders}</div>
              <ul>
                {p.features.map((feat) => (
                  <li key={feat}>{feat}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="landing__pricing-link">
          <a href="/pricing">See full pricing &rarr;</a>
        </div>
      </section>

      <section className="landing__section">
        <div className="landing__section-header">
          <h2>Frequently asked questions</h2>
        </div>
        <div className="landing__faq">
          <div className="landing__faq-item">
            <h3>Do I need a Shopify Partner account to install this?</h3>
            <p>
              No. Just enter your Shopify store URL above and approve the install on Shopify. The
              app works with any Shopify plan.
            </p>
          </div>
          <div className="landing__faq-item">
            <h3>Will this work with my Tally setup?</h3>
            <p>
              The app supports Tally Prime and Tally ERP 9. You configure ledger and tax mappings
              after install — no code or scripts required.
            </p>
          </div>
          <div className="landing__faq-item">
            <h3>What about historical orders?</h3>
            <p>
              You can backfill historical orders during onboarding. After that, new orders sync
              automatically as they come in.
            </p>
          </div>
          <div className="landing__faq-item">
            <h3>Is my store data safe?</h3>
            <p>
              We only access the order data needed to create Tally vouchers. All data is encrypted
              in transit and at rest, and you can revoke access from your Shopify admin at any
              time.
            </p>
          </div>
        </div>
      </section>

      <section className="landing__cta-banner">
        <h2>Ready to stop reconciling orders by hand?</h2>
        <p>Connect your store now — your first sync runs in minutes.</p>
        <InstallForm idPrefix="cta" />
      </section>

      <footer className="landing__footer">
        <div>
          &copy; {new Date().getFullYear()} Shopify Tally Sync &middot;{' '}
          <a href="mailto:suvojit@edesy.in">suvojit@edesy.in</a> &middot;{' '}
          <a href="/pricing">Pricing</a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
